import fp from 'lodash/fp';
import React, { useState, useCallback, useMemo, FC, ComponentType, useRef, PropsWithChildren } from 'react';
import { SortableContainer, SortableElement, SortableHandle, SortEndHandler } from 'react-sortable-hoc';
import arrayMove from 'array-move';

import {
    cn, Button, Text,
    Settings,
    Checkbox, localStorageAccessorJSON,
} from 'Modules/ui';
import { useTranslation } from 'Modules/core/i18n';

import { PopupWithButton, PopupWithButtonProps } from '../../PopupWithButton';
import { FactorinGridColumn } from '../common';
import { FactorinGridProps } from '../types';
import styles from './index.module.scss';


const DragElement = SortableHandle(() => (
    <div className={styles.dragHandle}>
        <div className={styles.handle} />
        <div className={styles.handle} />
        <div className={styles.handle} />
    </div>
));

type Column = {
    domainType: string;
    key: string;
    visible: boolean;
}

type ColumnProps = {
    data: Column;
    onChangeVisible: (index: number) => void;
    columnIndex: number;
}

const Column = SortableElement(({
    data: { domainType, key, visible },
    onChangeVisible,
    columnIndex,
}: ColumnProps) => {
    const { t } = useTranslation('domains');
    return (
        <div className={styles.column}>
            <Checkbox
                checked={visible}
                onChange={useCallback(
                    () => onChangeVisible(columnIndex),
                    [columnIndex, onChangeVisible],
                )}
            />
            <Text component='div' textType='light' className={styles.name}>
                {t(`${domainType}.columns.${key}`)}
            </Text>
            <DragElement />
        </div>
    );
});

type SortColumnsProps = {
    columns: Column[];
    onChangeVisible: ColumnProps['onChangeVisible'];
}

const SortColumns = SortableContainer(({ columns, onChangeVisible }: SortColumnsProps) => (
    <div>
        {columns.map((column, index) => (
            <Column
                key={column.key}
                index={index}
                data={column}
                columnIndex={index}
                onChangeVisible={onChangeVisible}
            />
        ))}
    </div>
));

const PopupButton: PopupWithButtonProps['button'] = ({ show, className, isOpen }) => (
    <Button
        variant='auxiliary'
        className={cn(
            styles.button,
            isOpen && styles.isOpen,
            className,
        )}
        onClick={show}
    >
        <Settings />
    </Button>
);

type ColumnSettinsProps = {
    classNames?: {
        container?: string;
        popup?: string;
        content?: string;
    };
    onSortEnd: SortEndHandler;
    columns: SortColumnsProps['columns'];
    onChangeVisible: SortColumnsProps['onChangeVisible'];
}

export const ColumnsSettings: FC<ColumnSettinsProps> = React.memo(({
    classNames = {}, columns,
    onSortEnd, onChangeVisible,
}) => (
    <PopupWithButton
        button={PopupButton}
        classNames={{
            container: cn(styles.container, classNames.container),
            popup: cn(styles.popup, classNames.popup),
            content: cn(styles.content, classNames.content),
        }}
    >
        <div className={styles.numbers}>
            {columns.map((_, idx) => (
                <Text
                    key={idx}
                    component='div'
                    textType='info'
                    className={styles.column}
                >
                    {idx + 1}
                </Text>
            ))}
        </div>
        <SortColumns
            columns={columns}
            onSortEnd={onSortEnd}
            onChangeVisible={onChangeVisible}
            helperClass={styles.dragged}
            lockAxis='y'
            lockToContainerEdges
            useDragHandle
        />
    </PopupWithButton>
));

type UseSortResult = {
    sortColumns: Column[];
    visibleColumns: FactorinGridColumn[];
    onSortEnd: ColumnSettinsProps['onSortEnd'];
    onChangeVisible: ColumnSettinsProps['onChangeVisible'];
}

export const useSort = (columns: FactorinGridColumn[], storageKey: string): UseSortResult => {
    const storage = useMemo(
        () => storageKey ? localStorageAccessorJSON<Column[]>(storageKey) : null,
        [storageKey],
    );

    const [sortColumns, changeSort] = useState(() => {
        if (storageKey) {
            const storageColumns = storage.get();
            if (storageColumns && fp.isEqual(
                fp.map(fp.get('field'), columns).sort(),
                fp.map(fp.get('key'), storageColumns).sort(),
            ))
                return storageColumns;
        }
        return columns.map<Column>(({ field, domainType, visible = true }) => ({
            key: field,
            domainType,
            visible,
        }));
    });

    const { current: memoColumns } = useRef(columns);

    const visibleColumns = useMemo(
        () => fp.pipe(
            fp.filter<Column>('visible'),
            fp.map(({ key }) => memoColumns.find(column => column.field === key)),
        )(sortColumns),
        [sortColumns, memoColumns],
    );

    const onSortEnd = useCallback<ColumnSettinsProps['onSortEnd']>(
        ({ oldIndex, newIndex }) => changeSort(oldColumns => {
            const newColumns = arrayMove(oldColumns, oldIndex, newIndex);

            if (storageKey)
                storage.set(newColumns);

            return newColumns;
        }),
        [storageKey, storage],
    );
    const onChangeVisible = useCallback<ColumnSettinsProps['onChangeVisible']>(
        columnIndex => changeSort(oldColumns => {
            const newColumns = oldColumns.map((column, index) => index === columnIndex
                ? {
                    ...column,
                    visible: !column.visible,
                }
                : column);

            if (storageKey)
                storage.set(newColumns);

            return newColumns;
        }),
        [storageKey, storage],
    );

    return {
        sortColumns,
        visibleColumns,
        onSortEnd,
        onChangeVisible,
    };
};

type WithSortColumnsSettings = Pick<FactorinGridProps, 'columns' | 'ratioSaveKey' | 'editableColumns'>;

type WithSettingsComponentProps<T> = PropsWithChildren<T> & {
    component: ComponentType<T>;
}

const WithSettingsComponent = <T extends WithSortColumnsSettings>(
    props: WithSettingsComponentProps<T>,
) => {
    const {
        sortColumns, visibleColumns,
        onChangeVisible, onSortEnd,
    } = useSort(props.columns, props.ratioSaveKey);
    return (
        <>
            <props.component
                {...props}
                columns={visibleColumns}
            />
            <ColumnsSettings
                columns={sortColumns}
                onChangeVisible={onChangeVisible}
                onSortEnd={onSortEnd}
            />
        </>
    );
};

export const withSortColumnsSettings = <T extends WithSortColumnsSettings>
(Component: ComponentType<T>) => (props: PropsWithChildren<T>) => props.editableColumns ? (
    <WithSettingsComponent
        component={Component}
        {...props}
    />
) : (
    <Component {...props} />
);
